// 모든 통신 전에 Token 값을 header에 설정함.
import axios, {AxiosError, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig} from "axios";
import {KEYS} from "@/app/constants/keys";
import {RouteUrl} from "@app/constants/routeUrl";
import {RefreshTokenPayload} from "@services/entities/memberEntity";
import {MemberRefreshTokenPostApi} from "@services/dataSource/memberApi";
import {SETTINGS} from "@app/constants/settings";
import {setAccessModalState} from "@services/stores/storeState";
import {RecoilPersistModel} from "@contents/models/commonModel";
import {apiClient} from "@services/dataSource/lib/api";

interface ApiQueueModel {
    config?: InternalAxiosRequestConfig<any>;
    resolve: (value?: unknown) => void;
    reject: (reason?: any) => void;
}

let isAccessing = false;
let retryQueue: ApiQueueModel[] = [];

export const makeSqiMetaData = (param?: any, data?: any) => {
    const recoilData = sessionStorage.getItem('recoil-persist');
    if(recoilData) {
        const recoilDataJson = JSON.parse(recoilData) as RecoilPersistModel;
        const metaArray: string[] = [];
        metaArray.push( `mid:${recoilDataJson?.spaceState?.activeRoute || ''}`);

        const isOp = (
            //1. space 선택으로 OP여부 체크
            ((recoilDataJson?.spaceState?.selectedOperation || '') === 'OP')
            //2. get params의 schema=base로 체크
            || (param && param['schema'] === 'base')
        );

        metaArray.push( `schm:${isOp ? 'base' : `${recoilDataJson?.spaceState?.space}`}`);

        return metaArray.join('|');
    } else {
        return '';
    }
}

export const checkRequest = async (config: InternalAxiosRequestConfig<any>) => {
    const spaceName = sessionStorage.getItem(KEYS.SPACE_NAME);

    if(spaceName != null && spaceName != '') {
        config.baseURL = SETTINGS.MNG_URL + spaceName;
    } else {
        config.baseURL = SETTINGS.BASE_URL;
    }

    const access_token = sessionStorage.getItem(KEYS.AUTH_KEY);

    if(access_token != null && access_token != '') {
        config.headers.Authorization = 'Bearer ' + access_token;
    }
    // config.headers.contentType = 'application/json';

    if(config.data && config.data['callback']) {
        delete config.data['callback'];
    }
    if(config.params && config.params['callback']) {
        delete config.params['callback'];
    }

    //set sqi meta data
    config.headers['X-SQI-META'] = makeSqiMetaData(config.params, config.data);

    return config;
}

export const checkRequestBase = async (config: InternalAxiosRequestConfig<any>) => {
    const baseConfig = await checkRequest(config);
    baseConfig.baseURL = SETTINGS.BASE_URL;

    return baseConfig;
}

export const checkRequestSpace  = async (config: InternalAxiosRequestConfig<any>) => {
    const spaceConfig = await checkRequest(config);
    const spaceName = sessionStorage.getItem(KEYS.SPACE_NAME);
    spaceConfig.baseURL = sessionStorage.getItem(KEYS.MEMBER_BRAND_URL) || SETTINGS.MNG_URL + spaceName;

    return spaceConfig;
}

export const checkBaseRequest = async (config: InternalAxiosRequestConfig<any>) => {

    if(config.data && config.data['callback']) {
        delete config.data['callback'];
    }
    if(config.params && config.params['callback']) {
        delete config.params['callback'];
    }

    return config;
}

// 모든 통신 결과(response) 결과의 status를 처리하는 interceptor
export const result = async (response: AxiosResponse<any, any>) => {
    if(response.status === 200 || response.status === 201) {
        if(response.data) {
            return response.data;
        } else {
            return Promise.reject(response);
        }
    } else {
        return Promise.reject(response);
    }
}

// 파일 다운로드 통신 결과(response) 결과의 status를 처리하는 interceptor
export const fileDownloadResult = async (response: AxiosResponse<any, any>) => {
    console.log(response);
    if(response.status === 200 || response.status === 201) {
        if(response) {
            return response;
        } else {
            return Promise.reject(response);
        }
    } else {
        return Promise.reject(response);
    }
}

// 모든 통신 오류에 대한 처리 interceptor
export const errorResult = async (error: AxiosError) => {
    // 401 api 권한 문제일 경우

    if(error.response && (error.response.status === 401 || (error.response?.status === 400 && error.code === 'LOGIN_USER_NOT_FOUND'))) {

        const access_token = sessionStorage.getItem(KEYS.AUTH_KEY);
        const refresh_token = sessionStorage.getItem(KEYS.REFRESH_KEY);

        // 토큰들이 없으면 reject
        if((access_token && access_token != '') && (refresh_token && refresh_token != '')) {
            sessionStorage.setItem(KEYS.AUTH_KEY, '');
            sessionStorage.setItem(KEYS.REFRESH_KEY, '');

            const createAuthConfig: AxiosRequestConfig = {
                headers: {
                    Authorization: `Bearer ${refresh_token}`,
                }
            }

            // refresh token으로 accessToken을 다시 가져옴.
            const result = await MemberRefreshTokenPostApi<RefreshTokenPayload>(createAuthConfig);

            if(result) {
                // 실패시 토근들을 지우고 로그인 페이지로 이동
                if(result.content?.accessToken && result.content?.refreshToken) {
                    sessionStorage.setItem(KEYS.AUTH_KEY, result.content.accessToken);
                    sessionStorage.setItem(KEYS.REFRESH_KEY, result.content.refreshToken);
                } else {
                    sessionStorage.setItem(KEYS.AUTH_KEY, '');
                    sessionStorage.setItem(KEYS.REFRESH_KEY, '');
                    window.location.href = RouteUrl.auth.login.url;
                }

                // config에 토큰을 재설정하고 다시 request 시도.
                if(error.config) {
                    // error.config.headers.contentType = 'application/json';
                    error.config.headers.Authorization = 'Bearer ' + result.content?.accessToken;

                    const originalResponse = await axios.request(error.config);
                    return originalResponse.data;
                } else {
                    return Promise.reject(error);
                }
            } else {
                return Promise.reject(error);
            }
        } else {
            // refresh도 실패하면 로그인 페이지로 보낸다.
            sessionStorage.setItem(KEYS.SPACE_NAME, '');
            sessionStorage.setItem(KEYS.AUTH_KEY, '');
            sessionStorage.setItem(KEYS.REFRESH_KEY, '');
            window.location.href = RouteUrl.auth.brandCode.url;
        }
    }

    // 403, access 거부일 경우(인사정보 access log 관련)
    if(error.response && (error.response.status === 403 &&
        (error.response.data as any)?.message === "ACCESS_IS_NOT_ALLOWED")
    ) {
        console.log('retryQuery - isAccessing', isAccessing);
        if(isAccessing) {
            return new Promise((resolve, reject) => {
                retryQueue.push({resolve, reject});
            }).then(() => {
                return apiClient(error.config!);
            });
        }
        setAccessModalState(true);
        isAccessing = true;

        return Promise.reject(error);
    }

    return Promise.reject(error);
}

export const resendAccessibleApi = () => {
    retryQueue.forEach(prom => {
        try{
            prom.resolve();
        }catch (e) {
            prom.reject(e);
        }
    });
    retryQueue = [];
    isAccessing = false;
}